import { Theme } from '@tribeplatform/design-system/types'
import { useI18n, T } from '@tribeplatform/react-components/i18n'
import { ColorCircle } from '@tribeplatform/react-ui-kit/ColorPicker'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import {
  colorThemeNames,
  isSupportedTheme,
  resolveColorTheme,
} from '@tribeplatform/react-ui-kit/ThemeProvider'

import { SidebarBackButton } from '../../../AdminMenu/SidebarBackButton.js'
import { SidebarContent } from '../../../AdminMenu/SidebarContent.js'
import { useCustomizerTheme } from '../../hooks/index.js'
import { StyleSelector } from './StyleSelector.js'

interface Props {
  onSelect: (theme: Theme) => void
  onClose: () => void
}

export const ColorPresets = ({ onSelect, onClose }: Props) => {
  const { $t } = useI18n()
  const { appearance } = useCustomizerTheme()

  const isPresetTheme = isSupportedTheme(appearance.theme.id)

  const updateColorTheme = async (newThemeId: string) => {
    onSelect(resolveColorTheme(newThemeId))
  }

  return (
    <SidebarContent
      header={<SidebarBackButton onClick={onClose} />}
      title={$t({
        defaultMessage: 'Palette presets',
        id: 'Admin.Sidebar.ColorsPresets',
      })}
      description={$t({
        defaultMessage:
          'The theme styles will be regenerated using the palette preset you choose.',
        id: 'Admin.Sidebar.ColorsPresetsDescription',
      })}
    >
      <RadioGroup
        value={appearance.theme.id}
        onChange={updateColorTheme}
        variant="cards"
      >
        <RadioGroup.Items>
          {!isPresetTheme && (
            <FormControl.Layout name="">
              <RadioGroup.Item
                key={appearance.theme.id}
                value={appearance.theme.id}
              >
                <StyleSelector
                  title={$t({
                    defaultMessage: 'Custom',
                    id: 'Admin.Sidebar.ColorsPresets.Custom',
                  })}
                  icon={
                    <div className="flex gap-2">
                      <ColorCircle color={appearance.theme.palette?.primary} />
                    </div>
                  }
                />
              </RadioGroup.Item>
              <FormControl.HelperText>
                <T
                  id="Admin.Sidebar.ColorsPresets.ChangeCustomThemeWarning"
                  defaultMessage=" If you change the color theme, your 'Custom' theme will be lost."
                />
              </FormControl.HelperText>
              <Divider padding="sm" />
            </FormControl.Layout>
          )}

          {colorThemeNames.map(name => {
            const colorTheme = resolveColorTheme(name)
            return (
              <RadioGroup.Item key={colorTheme.id} value={colorTheme.id}>
                <StyleSelector
                  title={colorTheme.name}
                  icon={
                    <div className="flex gap-2">
                      <ColorCircle color={colorTheme.palette?.primary} />
                    </div>
                  }
                />
              </RadioGroup.Item>
            )
          })}
        </RadioGroup.Items>
      </RadioGroup>
    </SidebarContent>
  )
}
